import { useEffect, useState, type DragEvent } from 'react';
import {
  addDoc,
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getBlob, ref, uploadBytes } from 'firebase/storage';
import { Download, Paperclip, Trash2 } from 'lucide-react';
import { db, storage } from '../lib/firebase';
import { useAuth } from '../auth/AuthProvider';
import { formatTimestamp } from '../lib/format';
import { Button, buttonStyles } from './ui/Button';
import { Alert } from './ui/Alert';
import { ReadFailure } from './ReadFailure';
import { listenQuery } from '../lib/liveRead';
import { useConfirm } from './ui/Confirm';
import type { Job, JobAttachment } from '../types/models';

interface Props {
  companyId: string;
  job: Job;
  archived?: boolean;
  canEdit: boolean;
  canRemove: boolean;
}

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.webp',
  '.gif',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.rtf',
  '.eml',
  '.msg',
].join(',');

export function AttachmentUploader({
  companyId,
  job,
  archived = false,
  canEdit,
  canRemove,
}: Props) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [attachments, setAttachments] = useState<JobAttachment[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);

  useEffect(() => {
    // PR-26: attachments live under the single jobs collection for the whole
    // job lifecycle (no copy on archive).
    const attachmentsRef = collection(
      db,
      'companies',
      companyId,
      'jobs',
      job.id,
      'attachments'
    );
    const q = query(attachmentsRef, orderBy('createdAt', 'desc'));
    setReadError(null);
    const unsub = listenQuery(
      q,
      (snap) => {
        setAttachments(
          snap.docs
            .map((attachmentDoc) => ({
              id: attachmentDoc.id,
              ...(attachmentDoc.data() as Omit<JobAttachment, 'id'>),
            }))
            .filter((attachment) => attachment.status !== 'deleted')
        );
        setReadError(null);
      },
      (err) => {
        console.error(err);
        setReadError(err.message);
      }
    );
    return () => unsub();
  }, [archived, companyId, job.id, readAttempt]);

  function addFiles(nextFiles: File[]) {
    const accepted = nextFiles.filter(
      (file) => file.size > 0 && file.size <= MAX_ATTACHMENT_BYTES
    );
    if (accepted.length !== nextFiles.length) {
      setError('Some files were skipped. Attachments must be under 25MB.');
    }
    setFiles((current) => [...current, ...accepted]);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    if (!canEdit || uploading) return;
    addFiles(Array.from(event.dataTransfer.files));
  }

  async function uploadSelected() {
    if (!user || files.length === 0) return;
    setUploading(true);
    setError(null);
    setNotice(null);

    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const attachmentId = crypto.randomUUID();
        const fileName = safeFileName(file.name || 'attachment');
        const storagePath = `companies/${companyId}/attachments/${job.id}/${attachmentId}/${fileName}`;
        const attachmentRef = doc(
          db,
          'companies',
          companyId,
          'jobs',
          job.id,
          'attachments',
          attachmentId
        );
        const metadata: Record<string, unknown> = {
          jobId: job.id,
          companyId,
          fileName,
          originalFileName: file.name || fileName,
          contentType: file.type || contentTypeFromName(fileName),
          sizeBytes: file.size,
          storagePath,
          kind: attachmentKind(fileName),
          status: 'pendingUpload',
          uploadedBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const displayName = user.displayName || user.email;
        if (displayName) metadata.uploadedByDisplayName = displayName;

        setProgress({ done: index, total: files.length });
        await setDoc(attachmentRef, metadata);
        try {
          await uploadBytes(ref(storage, storagePath), file, {
            contentType: String(metadata.contentType),
            customMetadata: {
              companyId,
              jobId: job.id,
              attachmentId,
              uploadedBy: user.uid,
            },
          });
          await updateDoc(attachmentRef, {
            status: 'available',
            updatedAt: serverTimestamp(),
          });
          // PR-26: no client audit write — the jobs auditLogs subcollection is
          // server-authored only. Attachment history needs a backend hook
          // (tracked with the PR-26 attachment-audit parity gap).
        } catch (uploadError) {
          await updateDoc(attachmentRef, {
            status: 'failed',
            updatedAt: serverTimestamp(),
          });
          throw uploadError;
        }
      }
      setFiles([]);
      setProgress(null);
      setNotice(`${files.length} attachment${files.length === 1 ? '' : 's'} uploaded.`);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  async function downloadAttachment(attachment: JobAttachment) {
    try {
      const blob = await getBlob(
        ref(storage, attachment.storagePath),
        MAX_ATTACHMENT_BYTES
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalFileName || attachment.fileName;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Could not download attachment.');
    }
  }

  async function removeAttachment(attachment: JobAttachment) {
    if (!canRemove || !user) return;
    const ok = await confirm({
      title: 'Remove attachment?',
      message: `Remove ${attachment.originalFileName}? It will no longer appear on this job.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await updateDoc(
        doc(
          db,
          'companies',
          companyId,
          'jobs',
          job.id,
          'attachments',
          attachment.id
        ),
        {
          status: 'deleted',
          deletedAt: serverTimestamp(),
          deletedBy: user.uid,
          updatedAt: serverTimestamp(),
        }
      );
      // PR-26: audit entry omitted — server-authored only on the jobs tree.
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Could not remove attachment.');
    }
  }

  return (
    <div className="space-y-4">
      {canEdit && (
        <div
          onDragEnter={(event) => { event.preventDefault(); if (!uploading) setIsDragging(true); }}
          onDragOver={(event) => { event.preventDefault(); if (!uploading) setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center gap-3 rounded-[12px] border-[1.5px] border-dashed p-5 text-center transition-colors duration-[180ms] ease-out ${
            isDragging ? 'border-accent bg-accenttint' : 'border-strong'
          }`}
        >
          <Paperclip size={22} strokeWidth={1.8} className="text-accent" aria-hidden />
          <div className="text-[15px] font-semibold text-ink">Drop emails, PDFs or documents here</div>
          {/* Attachments are paperwork. Evidence photographs go through the
              verified upload above, and the two are never mixed. */}
          <p className="text-[13px] text-ink3">
            Paperwork only. Photographs of the vehicle belong in Evidence, where the server verifies them.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2">
            <label className={`relative ${buttonStyles('secondary', 'sm')} cursor-pointer focus-within:ring-2 focus-within:ring-accent ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
              Choose files
              <input
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                multiple
                disabled={uploading}
                className="sr-only left-0 top-0"
                onChange={(event) => { addFiles(Array.from(event.target.files ?? [])); event.target.value = ''; }}
              />
            </label>
            <Button type="button" size="sm" disabled={files.length === 0} loading={uploading} onClick={uploadSelected}>
              {files.length > 0 ? `Upload ${files.length} file${files.length === 1 ? '' : 's'}` : 'Upload files'}
            </Button>
          </div>

          {files.length > 0 && (
            <ul className="flex w-full flex-wrap justify-center gap-2">
              {files.map((file) => (
                <li key={`${file.name}:${file.size}`}
                  className="inline-flex max-w-full items-center rounded-[8px] bg-[var(--tag-neutral-fill)] px-2.5 py-1 text-[13px] text-[var(--tag-neutral-ink)]">
                  <span className="max-w-56 truncate">{file.name}</span>
                </li>
              ))}
            </ul>
          )}

          {progress && (
            <div className="w-full max-w-sm">
              <div className="flex items-center justify-between text-[13px] text-ink2">
                <span>Uploading</span>
                <span className="font-mono">{progress.done + 1} / {progress.total}</span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-sunken">
                <div className="h-full rounded-full bg-st-progress transition-[width] duration-[180ms] ease-out"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
            </div>
          )}
          {notice && <p className="text-[13px] font-medium text-success-ink">{notice}</p>}
        </div>
      )}

      {error && <Alert tone="error">{error}</Alert>}
      {readError && <ReadFailure message={readError} retry={() => setReadAttempt((n) => n + 1)} />}

      {readError ? null : attachments.length === 0 ? (
        <p className="text-[14px] text-ink3">No attachments.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {attachments.map((attachment) => (
            <div key={attachment.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-hair bg-elev px-4 py-3">
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold text-ink">
                  {attachment.originalFileName || attachment.fileName}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-ink3">
                  <span className="font-mono">{formatFileSize(attachment.sizeBytes)}</span>
                  <span>{attachment.contentType}</span>
                  <span className="font-mono">{formatTimestamp(attachment.createdAt)}</span>
                  {attachment.status !== 'available' && (
                    <span className="font-semibold text-outstanding">{attachment.status}</span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button type="button" variant="secondary" size="sm" icon={Download}
                  disabled={attachment.status !== 'available'} onClick={() => downloadAttachment(attachment)}>
                  Download
                </Button>
                {canRemove && (
                  <Button type="button" variant="danger-outline" size="sm" icon={Trash2}
                    onClick={() => removeAttachment(attachment)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function safeFileName(name: string) {
  const cleaned = name
    .trim()
    .replace(/[^\w.\-() ]+/g, '_')
    .replace(/\s+/g, ' ')
    .slice(0, 140);
  return cleaned || 'attachment';
}

function contentTypeFromName(name: string) {
  const ext = name.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
    txt: 'text/plain',
    rtf: 'application/rtf',
    eml: 'message/rfc822',
    msg: 'application/vnd.ms-outlook',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    heic: 'image/heic',
    webp: 'image/webp',
    gif: 'image/gif',
  };
  return (ext && map[ext]) || 'application/octet-stream';
}

function attachmentKind(name: string): 'file' | 'email' {
  const ext = name.split('.').pop()?.toLowerCase();
  return ext === 'eml' || ext === 'msg' ? 'email' : 'file';
}

function formatFileSize(sizeBytes: number) {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
